<template>
  <button
    class="tile"
    :class="{
      'tile--selectable': selectable,
      'tile--selected': selectable && selected,
      'tile--vertical': layout === 'vertical',
    }"
    type="button"
    :disabled="disabled"
    :role="selectable ? 'radio' : undefined"
    :aria-checked="selectable ? selected : undefined"
    v-bind="$attrs"
    @click="onClick"
  >
    <!-- radio indicator, only rendered when the tile behaves as a selectable (radio) tile -->
    <div v-if="selectable"  class="tile__checkbox" aria-hidden="true">
      <v-radio
        :model-value="selected"
        :disabled="disabled"
        readonly
        density="compact"
      />
    </div>

    <!-- @slot left visual, overrides the icon prop -->
    <span v-if="$slots['left-icon'] || icon" class="tile__visual" :style="visualStyle">
      <slot name="left-icon">
        <app-material-icon
          :name="icon"
          :size="20"
          :color="resolvedIconColor"
          :filled="iconFilled"
        />
      </slot>
    </span>

    <span class="tile__content">
      <span class="tile__heading">
        <!-- @slot heading content, overrides the label prop -->
        <slot name="heading">
          <span class="tile__label heading-xs-bold">{{ label }}</span>
        </slot>
        <!-- @slot optional badge displayed next to the label -->
        <slot name="badge"></slot>
      </span>
      <!-- @slot caption content, overrides the caption prop -->
      <span v-if="$slots['caption'] || caption" class="tile__caption detail-md-regular">
        <slot name="caption">{{ caption }}</slot>
      </span>
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import type { ThemeColors } from '@/plugins/styles/colors'

import type { TileLayout } from './types'

/**
 * @displayName Tile
 * A large clickable button with a label, optional caption and optional leading icon.
 */
export default defineComponent({
  name: 'AppTile',
  inheritAttrs: false,
  props: {
    /**
     * Main heading text of the tile
     */
    label: {
      type: String,
      default: '',
    },
    /**
     * Secondary helper text displayed under the label
     */
    caption: {
      type: String,
      default: '',
    },
    /**
     * Material icon name displayed in the leading visual
     */
    icon: {
      type: String,
      default: '',
    },
    /**
     * Theme color token of the leading icon
     * @example "icon-primary", "icon-info"
     */
    iconColor: {
      type: String as PropType<ThemeColors>,
      default: 'icon-primary',
    },
    /**
     * Adds the "filled" prop to the icon
     */
    iconFilled: {
      type: Boolean,
      default: false,
    },
    /**
     * Theme color token of the leading visual background
     * @example "surface-grey-subtle", "surface-info-subtle"
     */
    iconBackgroundColor: {
      type: String as PropType<ThemeColors>,
      default: 'surface-grey-subtle',
    },
    /**
     * Disables the tile
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Turns the tile into a selectable (radio) tile: renders a leading radio
     * indicator, applies the selected/unselected styling and exposes
     * `role="radio"` + `aria-checked`. When false, the tile stays a plain button.
     */
    selectable: {
      type: Boolean,
      default: false,
    },
    /**
     * Selected state of a selectable tile (ignored when `selectable` is false)
     */
    selected: {
      type: Boolean,
      default: false,
    },
    /**
     * Visual layout of the tile.
     * - `horizontal` (default): visual and content on a single row.
     * - `vertical`: visual and radio indicator on a top row, heading and caption
     *   stacked underneath.
     */
    layout: {
      type: String as PropType<TileLayout>,
      default: 'horizontal',
    },
  },
  emits: ['click'],
  computed: {
    resolvedIconColor (): string {
      return this.disabled ? 'rgb(var(--v-theme-icon-disabled))' : `rgb(var(--v-theme-${this.iconColor}))`
    },
    visualStyle (): Record<string, string> {
      return { backgroundColor: `rgb(var(--v-theme-${this.iconBackgroundColor}))` }
    },
  },
  methods: {
    onClick (event: MouseEvent) {
      this.$emit('click', event)
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.tile {
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  column-gap: 16px;
  width: 100%;
  padding: 16px;
  border: get-border('border-secondary');
  @include border-radius('border-radius-3');
  background-color: rgb(var(--v-theme-surface-primary));
  text-align: left;
  cursor: pointer;

  // overlay used for hover / focus / pressed background tint
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
  }

  &:hover:enabled,
  &:focus-visible:enabled {
    border-color: rgb(var(--v-theme-border-secondary-hover));

    &::after {
      background-color: rgba(var(--v-theme-surface-hover));
    }
  }

  &:active:enabled {
    border-color: rgb(var(--v-theme-border-secondary-hover));

    &::after {
      background-color: rgba(var(--v-theme-surface-pressed));
    }
  }

  &:focus-visible:enabled {
    outline: none;
    box-shadow: 0 0 0 3px rgb(var(--v-theme-sky-400)); // focus-ring
  }

  &:disabled {
    border-color: rgb(var(--v-theme-border-disabled));
    background-color: rgb(var(--v-theme-surface-disabled));
    cursor: default;

    .tile__label,
    .tile__caption {
      color: rgb(var(--v-theme-text-disabled));
    }

    .tile__radio {
      border-color: rgb(var(--v-theme-border-disabled));
    }
  }
}

// keep the info border/background on hover so the selected state does not fall
// back to the neutral hover border defined on the base .tile
.tile--selected,
.tile--selected:hover:enabled,
.tile--selected:active:enabled {
  @include border-width('border-width-2');
  border-color: rgb(var(--v-theme-border-info-bold));
  background-color: rgb(var(--v-theme-surface-info));
}

// vertical layout: leading visual + radio indicator share a top row
// (visual left, radio right), heading and caption stack underneath.
// Grid placement keeps the shared markup untouched so the horizontal
// (default) layout above is not affected.
.tile--vertical {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'visual radio'
    'content content';
  align-items: start;
  gap: 16px;

  .tile__visual {
    grid-area: visual;
  }

  .tile__checkbox {
    grid-area: radio;
    justify-self: end;
  }

  .tile__content {
    grid-area: content;
  }
}

.tile__visual {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  @include border-radius('border-radius-2');
  overflow: hidden;
}

.tile__content {
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  row-gap: 2px;
}

.tile__heading {
  display: flex;
  align-items: center;
  column-gap: 8px;
}

.tile__label {
  overflow: hidden;
  color: rgb(var(--v-theme-text-primary));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tile__caption {
  color: rgb(var(--v-theme-text-tertiary));
}

.tile__checkbox :deep(.v-selection-control__wrapper) {
  width: auto;
  height: auto;
}
</style>
